import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";

import Dashboard from "@/pages/Dashboard";
import AdminPanel from "@/pages/AdminPanel";
import LoginPage from "@/pages/LoginPage";
import KanbanRedirect from "@/pages/KanbanRedirect";
import PendingApproval from "@/pages/PendingApproval";
import { API, APP_BASE_PATH } from "@/lib/api";

const ROUTER_BASENAME = APP_BASE_PATH;

axios.defaults.withCredentials = true;

function useAuthState() {
  const [authState, setAuthState] = useState({
    isLoading: true,
    isAuthenticated: false,
    user: null,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        if (!isMounted) {
          return;
        }
        setAuthState({
          isLoading: false,
          isAuthenticated: true,
          user: response.data,
        });
      } catch {
        if (!isMounted) {
          return;
        }
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          user: null,
        });
      }
    };

    fetchUser();
    return () => {
      isMounted = false;
    };
  }, []);

  return authState;
}

function ProtectedRoute({ children, requireAdmin = false }) {
  const location = useLocation();
  const authState = useAuthState();

  if (authState.isLoading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!authState.isAuthenticated) {
    const redirect = encodeURIComponent(`${location.pathname}${location.search || ""}`);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  if (requireAdmin && authState.user?.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  if (!requireAdmin && authState.user?.role !== "admin" && !authState.user?.is_approved) {
    return <Navigate to="/pending-approval" replace />;
  }

  return children(authState.user);
}

function PendingApprovalRoute() {
  const authState = useAuthState();

  if (authState.isLoading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!authState.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (authState.user?.role === "admin" || authState.user?.is_approved) {
    return <Navigate to="/dashboard" replace />;
  }

  return <PendingApproval user={authState.user} />;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {(user) => <Dashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/pending-approval" element={<PendingApprovalRoute />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            {(user) => <AdminPanel user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/kanban"
        element={
          <ProtectedRoute requireAdmin>
            {() => <KanbanRedirect />}
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter basename={ROUTER_BASENAME || undefined}>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
